import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  MenuItem,
  LinearProgress,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Chip,
} from '@mui/material';
import { ContentType, ClassId } from '../types';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DownloadIcon from '@mui/icons-material/Download';
import { generateVideoAccess, getVideoTaskStatus, getUserVideoGenerationInfo, VideoTaskResponse } from '../services/api';

interface VideoGenerationTabProps {
  contentType?: ContentType;
  classId?: ClassId;
  sessionId?: string;
  userId?: number;
  accessId?: string;
}

const contentDescriptions: Record<ContentType, string> = {
  'Normal': '',
  'AI 크리에이터': '창의적인 영상을 생성하고 미디어 콘텐츠를 만들어보세요.',
  'AI 윤리 & 사회변화': 'AI 영상 생성이 가져올 사회적 영향을 탐구해보세요.',
  '차세대 기업가 정신': '비즈니스에 활용할 수 있는 영상을 생성해보세요.',
  '미래 사회 탐험가': '미래 사회의 모습을 영상으로 표현해보세요.',
  '미스터리 코드': '코딩과 관련된 영상을 생성해보세요.',
  'AI 트랜드 익스플로러': 'AI 트랜드를 탐험하는데 필요한 영상을 만들어보세요.',
};

interface GeneratedVideo {
  id: string;
  videoUrl: string;
  promptText: string;
  ratio: string;
  duration: number;
  createdAt: number; // timestamp로 저장
  status: 'generating' | 'completed' | 'failed';
  taskId?: string;
  progress?: number; // 진행률 0-100
  currentStep?: string; // 현재 단계
}

const VideoGenerationTab: React.FC<VideoGenerationTabProps> = ({ 
  contentType = 'Normal', 
  classId, 
  sessionId, 
  userId,
  accessId 
}) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedImageFile, setUploadedImageFile] = useState<File | null>(null);
  const [promptText, setPromptText] = useState('');
  const [ratio, setRatio] = useState('1280:720');
  const [duration, setDuration] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedVideos, setGeneratedVideos] = useState<GeneratedVideo[]>(() => {
    const savedVideos = localStorage.getItem(`generated_videos_${contentType}`);
    return savedVideos ? JSON.parse(savedVideos) : [];
  });
  const [dragActive, setDragActive] = useState(false);
  const [videoGenerationInfo, setVideoGenerationInfo] = useState<{
    current_count: number;
    max_count: number;
    remaining_count: number;
  } | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const ratioOptions = [
    { value: '1280:720', label: '16:9 (가로)' },
    { value: '720:1280', label: '9:16 (세로)' },
    { value: '1024:1024', label: '1:1 (정사각형)' },
  ];

  const durationOptions = [
    { value: 2, label: '2초' },
    { value: 5, label: '5초' },
    { value: 10, label: '10초' },
  ];

  // 생성된 비디오가 변경될 때마다 로컬 스토리지에 저장
  useEffect(() => {
    localStorage.setItem(`generated_videos_${contentType}`, JSON.stringify(generatedVideos));
  }, [generatedVideos, contentType]);

  // contentType이 변경될 때 해당 타입의 저장된 비디오 불러오기
  useEffect(() => {
    const savedVideos = localStorage.getItem(`generated_videos_${contentType}`);
    if (savedVideos) {
      setGeneratedVideos(JSON.parse(savedVideos));
    } else {
      setGeneratedVideos([]);
    }
  }, [contentType]);

  // 액세스 코드 기반일 때 영상 생성 횟수 정보 로드
  useEffect(() => {
    const loadVideoGenerationInfo = async () => {
      if (accessId) {
        try {
          const info = await getUserVideoGenerationInfo(accessId);
          setVideoGenerationInfo(info);
        } catch (error) {
          console.error('영상 생성 정보 로드 실패:', error);
        }
      }
    };

    loadVideoGenerationInfo();
  }, [accessId]);

  // 진행 중인 비디오의 메시지를 실시간으로 업데이트
  useEffect(() => {
    const interval = setInterval(() => {
      setGeneratedVideos(prev => prev.map(video => {
        if (video.status === 'generating') {
          const progressInfo = getProgressMessage(video);
          return {
            ...video,
            progress: progressInfo.progress,
            currentStep: progressInfo.step
          };
        }
        return video;
      }));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // 파일을 base64로 변환하는 함수
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  // 파일 업로드 처리
  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('이미지 파일만 업로드할 수 있습니다.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB 제한
      setError('파일 크기는 10MB를 초과할 수 없습니다.');
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      setUploadedImage(base64);
      setUploadedImageFile(file);
      setError(null);
    } catch (error) {
      setError('이미지 업로드에 실패했습니다.');
    }
  };

  // 드래그 앤 드랍 핸들러
  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  }, []);

  // 파일 선택 핸들러
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // 이미지 삭제
  const handleRemoveImage = () => {
    setUploadedImage(null);
    setUploadedImageFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 비디오 생성 상태 체크
  const checkVideoStatus = async (taskId: string, videoId: string) => {
    try {
      const response = await getVideoTaskStatus(taskId);
      
      setGeneratedVideos(prev => 
        prev.map(video => {
          if (video.id === videoId) {
            if (response.success && response.video_url) {
              return {
                ...video,
                status: 'completed',
                videoUrl: response.video_url,
              };
            } else if (response.error) {
              return {
                ...video,
                status: 'failed',
              };
            }
          }
          return video;
        })
      );

      // 생성 중인 경우 1초 후 다시 체크
      if (response.status === 'processing' || response.status === 'queued') {
        setTimeout(() => checkVideoStatus(taskId, videoId), 1000);
      }
    } catch (error) {
      console.error('비디오 상태 체크 실패:', error);
    }
  };

  // 비디오 생성
  const handleGenerateVideo = async () => {
    if (!uploadedImage || !promptText.trim()) {
      setError('이미지와 프롬프트를 모두 입력해주세요.');
      return;
    }

    if (!accessId) {
      setError('액세스 정보가 없습니다.');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const response: VideoTaskResponse = await generateVideoAccess(
        uploadedImage,
        promptText,
        accessId,
        ratio,
        duration
      );

      if (response.success) {
        const newVideo: GeneratedVideo = {
          id: Date.now().toString(),
          videoUrl: response.video_url || '',
          promptText: response.prompt_text || promptText,
          ratio: response.ratio || ratio,
          duration: response.duration || duration,
          createdAt: Date.now(),
          status: response.video_url ? 'completed' : 'generating',
          taskId: response.task_id,
        };

        setGeneratedVideos(prev => [newVideo, ...prev]);

        // 비디오가 아직 생성 중인 경우 상태 체크 시작
        if (response.task_id && !response.video_url) {
          checkVideoStatus(response.task_id, newVideo.id);
        }

        // 영상 생성 횟수 정보 새로고침
        if (accessId) {
          try {
            const updatedInfo = await getUserVideoGenerationInfo(accessId);
            setVideoGenerationInfo(updatedInfo);
          } catch (error) {
            console.error('영상 생성 정보 새로고침 실패:', error);
          }
        }

        // 입력 필드 초기화
        setPromptText('');
        setUploadedImage(null);
        setUploadedImageFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setError(response.error || '비디오 생성에 실패했습니다.');
      }
    } catch (error: any) {
      setError(error.message || '비디오 생성 중 오류가 발생했습니다.');
    } finally {
      setIsGenerating(false);
    }
  };

  // 비디오 다운로드
  const handleDownloadVideo = (videoUrl: string) => {
    const link = document.createElement('a');
    link.href = videoUrl;
    link.download = `generated-video-${Date.now()}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 비디오 삭제
  const handleDeleteVideo = (videoId: string) => {
    setGeneratedVideos(prev => prev.filter(video => video.id !== videoId));
  };

  // 시간 경과에 따른 동적 메시지 생성
  const getProgressMessage = (video: GeneratedVideo) => {
    const elapsed = Math.floor((Date.now() - video.createdAt) / 1000);
    
    if (elapsed < 30) {
      return {
        step: "이미지 분석 중",
        message: "업로드된 이미지를 AI가 분석하고 있습니다",
        progress: 15
      };
    } else if (elapsed < 60) {
      return {
        step: "프롬프트 처리 중",
        message: "텍스트 프롬프트를 해석하고 있습니다",
        progress: 30
      };
    } else if (elapsed < 120) {
      return {
        step: "영상 생성 중",
        message: "AI가 영상을 생성하고 있습니다",
        progress: 60
      };
    } else {
      return {
        step: "최종 처리 중",
        message: "영상을 최적화하고 있습니다",
        progress: 90
      };
    }
  };

  return (
    <Box 
      sx={{ 
        height: 'calc(100vh - 160px)',
        maxHeight: 'calc(100vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        p: 2,
      }}
    >
      {/* 고정 헤더 */}
      <Typography 
        variant="h5" 
        sx={{
          color: 'white',
          fontWeight: 600,
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
          mb: 1,
          flexShrink: 0,
        }}
      >
        🎬 이미지에서 영상 생성
      </Typography>

      {contentDescriptions[contentType] && (
        <Typography 
          variant="body1" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)', 
            mb: 2,
            lineHeight: 1.6,
            flexShrink: 0,
          }}
        >
          {contentDescriptions[contentType]}
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* 메인 콘텐츠 영역 */}
      <Box 
        sx={{
          flex: 1,
          display: 'flex',
          gap: 2,
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* 왼쪽: 이미지 업로드 + 영상 설정 (스크롤 없음) */}
        <Box 
          sx={{
            flex: 2,
            minWidth: '320px',
            maxWidth: '500px',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            overflow: 'hidden',
          }}
        >
          {/* 이미지 업로드 섹션 */}
          <Paper
            sx={{
              p: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              flex: 1.2,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem', flexShrink: 0 }}>
              1. 이미지 업로드
            </Typography>
            
            <Box sx={{ 
              width: '100%', 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column'
            }}>
              {!uploadedImage ? (
                <Box
                  ref={dropRef}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  sx={{
                    border: `2px dashed ${dragActive ? '#4caf50' : 'rgba(255, 255, 255, 0.3)'}`,
                    borderRadius: '8px',
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    background: dragActive ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                    width: '93%',
                    flex: 1,
                    minHeight: '160px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      borderColor: '#4caf50',
                      background: 'rgba(76, 175, 80, 0.1)',
                    },
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <CloudUploadIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: 'white', mb: 1.5 }} />
                  <Typography variant="body2" sx={{ color: 'white', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    이미지를 드래그하거나 클릭
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    JPG, PNG, GIF (최대 10MB)
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  position: 'relative', 
                  width: '100%', 
                  flex: 1,
                  minHeight: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  borderRadius: '8px',
                }}>
                  <img
                    src={uploadedImage}
                    alt="업로드된 이미지"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      width: '32px',
                      height: '32px',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.3)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
            </Box>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Paper>

          {/* 영상 설정 섹션 */}
          <Paper
            sx={{
              p: 2,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: '12px',
              flex: 0.8,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', mb: 1.5, fontSize: '1rem' }}>
              2. 영상 설정
            </Typography>
            
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                fullWidth
                label="프롬프트"
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder="어떤 영상을 생성하고 싶나요?"
                multiline
                rows={2}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '& fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.4)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#4caf50',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-focused': {
                      color: '#4caf50',
                    },
                  },
                }}
              />
              
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="비율"
                    value={ratio}
                    onChange={(e) => setRatio(e.target.value)}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4caf50',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&.Mui-focused': {
                          color: '#4caf50',
                        },
                      },
                    }}
                  >
                    {ratioOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    select
                    label="길이"
                    value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '8px',
                        color: 'white',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.4)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#4caf50',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        '&.Mui-focused': {
                          color: '#4caf50',
                        },
                      },
                    }}
                  >
                    {durationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>
              
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateVideo}
                disabled={
                  isGenerating || 
                  !uploadedImage || 
                  !promptText.trim() || 
                  (videoGenerationInfo?.remaining_count === 0)
                }
                startIcon={isGenerating ? <CircularProgress size={16} /> : <VideoLibraryIcon />}
                sx={{
                  height: { xs: '40px', sm: '44px' },
                  borderRadius: '8px',
                  background: (videoGenerationInfo?.remaining_count === 0)
                    ? 'rgba(158, 158, 158, 0.3)'
                    : 'linear-gradient(45deg, #4caf50, #66bb6a)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  '&:hover': {
                    background: (videoGenerationInfo?.remaining_count === 0)
                      ? 'rgba(158, 158, 158, 0.3)'
                      : 'linear-gradient(45deg, #45a049, #5db75d)',
                  },
                  '&:disabled': {
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.5)',
                  },
                }}
              >
                {isGenerating ? '생성 중...' : 
                 videoGenerationInfo?.max_count === 0 ? '영상 생성 비활성화' :
                 videoGenerationInfo?.remaining_count === 0 ? '생성 횟수 초과' :
                 videoGenerationInfo ? `영상 생성 (${videoGenerationInfo.remaining_count}회 남음)` :
                 '영상 생성'}
              </Button>
              
              {/* 영상 생성 횟수 정보 표시 */}
              {videoGenerationInfo && (
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: { xs: '0.6rem', sm: '0.75rem' } }}>
                    {videoGenerationInfo?.max_count === 0 ? (
                      '영상 생성 기능이 비활성화되어 있습니다'
                    ) : (
                      `사용 가능한 영상 생성: ${videoGenerationInfo?.remaining_count}/${videoGenerationInfo?.max_count}회`
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>

        {/* 오른쪽: 생성된 영상 갤러리 (스크롤 있음) */}
        <Box
          sx={{
            flex: 3,
            minWidth: '300px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* 스크롤 가능한 영상 갤러리 */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255, 255, 255, 0.3)',
                borderRadius: '3px',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.4)',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                pr: 1,
              }}
            >
              {generatedVideos.length === 0 ? (
                <Paper
                  sx={{
                    p: 4,
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    textAlign: 'center',
                  }}
                >
                  <VideoLibraryIcon sx={{ fontSize: { xs: 32, sm: 48 }, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    아직 생성된 영상이 없습니다
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.4)', mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                    왼쪽에서 이미지를 업로드하고 프롬프트를 입력해보세요
                  </Typography>
                </Paper>
              ) : (
                generatedVideos.map((video) => (
                  <Card
                    key={video.id}
                    sx={{
                      width: '100%',
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(20px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      borderRadius: '12px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
                      },
                    }}
                  >
                      {video.status === 'completed' ? (
                        <Box sx={{ width: '100%', aspectRatio: '16/9' }}>
                          <CardMedia
                            component="video"
                            src={video.videoUrl}
                            controls
                            sx={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '8px 8px 0 0',
                            }}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            aspectRatio: '16/9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px 8px 0 0',
                          }}
                        >
                          {video.status === 'generating' ? (
                            <>
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mb: 2,
                                }}
                              >
                                {/* 외부 펄싱 원 */}
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    width: { xs: '50px', sm: '60px' },
                                    height: { xs: '50px', sm: '60px' },
                                    borderRadius: '50%',
                                    background: 'linear-gradient(45deg, #4caf50, #66bb6a)',
                                    opacity: 0.3,
                                    animation: 'pulse 2s ease-in-out infinite',
                                    '@keyframes pulse': {
                                      '0%': {
                                        transform: 'scale(1)',
                                        opacity: 0.3,
                                      },
                                      '50%': {
                                        transform: 'scale(1.1)',
                                        opacity: 0.1,
                                      },
                                      '100%': {
                                        transform: 'scale(1)',
                                        opacity: 0.3,
                                      },
                                    },
                                  }}
                                />
                                {/* 진행률 표시 */}
                                <CircularProgress
                                  variant="determinate"
                                  value={video.progress || 0}
                                  thickness={3}
                                  sx={{
                                    color: '#4caf50',
                                    position: 'absolute',
                                    width: { xs: '35px', sm: '45px' },
                                    height: { xs: '35px', sm: '45px' },
                                    '& .MuiCircularProgress-circle': {
                                      strokeLinecap: 'round',
                                    },
                                  }}
                                />
                                {/* 회전하는 원 */}
                                <CircularProgress
                                  thickness={3}
                                  sx={{
                                    color: 'rgba(76, 175, 80, 0.3)',
                                    width: { xs: '35px', sm: '45px' },
                                    height: { xs: '35px', sm: '45px' },
                                    '& .MuiCircularProgress-circle': {
                                      strokeLinecap: 'round',
                                    },
                                  }}
                                />
                                {/* 중앙 아이콘 */}
                                <VideoLibraryIcon
                                  sx={{
                                    position: 'absolute',
                                    fontSize: { xs: 16, sm: 20 },
                                    color: '#4caf50',
                                    animation: 'fadeInOut 2s ease-in-out infinite',
                                    '@keyframes fadeInOut': {
                                      '0%': { opacity: 0.6 },
                                      '50%': { opacity: 1 },
                                      '100%': { opacity: 0.6 },
                                    },
                                  }}
                                />
                              </Box>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'white', 
                                  mb: 1,
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  animation: 'textGlow 2s ease-in-out infinite',
                                  '@keyframes textGlow': {
                                    '0%': { textShadow: '0 0 5px rgba(76, 175, 80, 0.5)' },
                                    '50%': { textShadow: '0 0 20px rgba(76, 175, 80, 0.8)' },
                                    '100%': { textShadow: '0 0 5px rgba(76, 175, 80, 0.5)' },
                                  },
                                }}
                              >
                                🎬 {video.currentStep || '영상 생성 중...'}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  textAlign: 'center',
                                  lineHeight: 1.2,
                                  mb: 1.5,
                                  px: 2,
                                  fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                }}
                              >
                                {getProgressMessage(video).message}
                              </Typography>
                              {/* 진행률 텍스트 */}
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: '#4caf50',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: 1,
                                  fontSize: { xs: '0.6rem', sm: '0.75rem' },
                                }}
                              >
                                <span>{video.progress || 0}% 완료</span>
                                •
                                <span>
                                  {Math.floor((Date.now() - video.createdAt) / 1000)}초 경과
                                </span>
                              </Typography>
                            </>
                          ) : (
                            <>
                              <VideoLibraryIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'rgba(255, 255, 255, 0.5)', mb: 2 }} />
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                생성 실패
                              </Typography>
                            </>
                          )}
                        </Box>
                      )}
                      
                      <CardContent sx={{ color: 'white', p: 2 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          gap: 2 
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              opacity: 0.8, 
                              flex: 1,
                              wordBreak: 'break-word',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            }}
                          >
                            {video.promptText}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                            {video.status === 'completed' && (
                              <Button
                                size="small"
                                startIcon={<DownloadIcon fontSize="small" />}
                                onClick={() => handleDownloadVideo(video.videoUrl)}
                                sx={{
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  color: 'white',
                                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                  minWidth: 'auto',
                                  px: 1,
                                  '&:hover': {
                                    background: 'rgba(255, 255, 255, 0.2)',
                                  },
                                }}
                              >
                                다운로드
                              </Button>
                            )}
                            <Button
                              size="small"
                              startIcon={<DeleteIcon fontSize="small" />}
                              onClick={() => handleDeleteVideo(video.id)}
                              sx={{
                                background: 'rgba(244, 67, 54, 0.2)',
                                color: 'white',
                                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                                minWidth: 'auto',
                                px: 1,
                                '&:hover': {
                                  background: 'rgba(244, 67, 54, 0.3)',
                                },
                              }}
                            >
                              삭제
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                ))
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default VideoGenerationTab; 